/**
 * EditPaymentInfo Component
 *
 * Description: This component allows users to edit their payment information, including account holder name, bank details, and routing information.
 * It handles form submission, manages loading state, and uses a callback function to pass updated information upon successful submission.
 *
 * File Name: EditPaymentInfo.tsx
 * Date: 27-09-2024
 */

import React, { FormEvent, useEffect, useState } from "react";
import SimpleInput from "../SimpleInput";
import PrimaryBtn from "../GlobalComponents/primaryBtn";
import { IAccountInfo } from "src/types/GlobalInterfaces";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { typeValidator, validateFirstName } from "src/helpers/helperFunctions";
import {
  pakistanSwiftCodeRegex,
  pakistanBranchCodeRegex,
  pakistanAccountNumberRegex,
  pakistanIbanRegex,
} from "src/lib/constans";

interface inputItems {
  label: string;
  required: boolean;
  type: "text" | "number";
  key: keyof IAccountInfo;
  minLength: number | undefined;
  maxLength: number | undefined;
  validate: string
}

const EditPaymentInfo: React.FC<{
  accountInfo: IAccountInfo | null;
  onSuccess: (obj: IAccountInfo) => void;
  isOperatorEdit?: boolean;
  uid?: string;
  extraContainerClasses?: string;
}> = ({
  accountInfo = null,
  onSuccess = () => {},
  isOperatorEdit = false,
  uid = "",
  extraContainerClasses = "",
}) => {
  const allInputs: inputItems[] = [
    {
      label: "Account Holder Name",
      required: true,
      type: "text",
      key: "accountHolderName",
      validate: "alphabets",
      minLength: undefined,
      maxLength: 70,
    },
    {
      label: "Bank Account Number",
      required: true,
      type: "text",
      key: "accountNumber",
      validate: "alphanumeric",
      minLength: 10,
      maxLength: 16,
    },
    {
      label: "Bank Name",
      required: true,
      type: "text",
      key: "bankName",
      validate: "alphanumeric",
      minLength: undefined,
      maxLength: 50,
    },
    {
      label: "Bank Branch Code",
      required: true,
      type: "text",
      key: "bankBranchCode",
      validate: "alphanumeric",
      minLength: 4,
      maxLength: 4,
    },
    {
      label: "BIC / Swift Code",
      required: true,
      type: "text",
      key: "swiftCode",
      validate: "alphanumeric",
      minLength: 8,
      maxLength: 14,
    },
    {
      label: "IBAN",
      required: true,
      type: "text",
      key: "iban",
      validate: "alphanumeric",
      minLength: 24,
      maxLength: 29,
    },
  ];

  
  const handleChange = (key: keyof IAccountInfo, val: string , validate : string) => {
    if (key === "swiftCode") {
      val = val.toUpperCase();
      val = typeValidator(val , validate as any)
      val = val.replace(/\s/g, "");
      
      let formattedSwiftCode = "";
      
      if (val.length > 0) {
        formattedSwiftCode += val.slice(0, 4);
        
        if (val.length > 4) {
          formattedSwiftCode += " " + val.slice(4, 6);
          
          if (val.length > 6) {
            formattedSwiftCode += " " + val.slice(6, 8);
            
            if (val.length > 8) {
              formattedSwiftCode += " " + val.slice(8);
            }
          }
        }
      }

      setEditState((prev) => {
        const prevState = prev || ({} as IAccountInfo);
        return {
          ...prevState,
          [key]: formattedSwiftCode,
        };
      });
    } else if (key === "iban") {
      val = val.toUpperCase();
      val = typeValidator(val , validate as any)
      val = val.replace(/\s/g, "");
      
      let formattedIban = "";
      for (let i = 0; i < val.length; i += 4) {
        if (i > 0) {
          formattedIban += " ";
        }
        formattedIban += val.slice(i, i + 4);
      }

      setEditState((prev) => {
        const prevState = prev || ({} as IAccountInfo);
        return {
          ...prevState,
          [key]: formattedIban,
        };
      });
    } else if (key === "accountNumber") {
      val = val.replace(/\s/g, "");
      val = val.replace(/[^0-9]/g, "");
      setEditState((prev) => {
        const prevState = prev || ({} as IAccountInfo);
        return {
          ...prevState,
          [key]: val,
        };
      });
    } else {
      setEditState((prev) => {
        const prevState = prev || ({} as IAccountInfo);
        return {
          ...prevState,
          [key]: val,
        };
      });
    }
  };

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [editState, setEditState] = useState<IAccountInfo | null>(accountInfo);
  const [error, setError] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    if (!editState) return false;

    if (!editState.accountHolderName?.trim()) {
      newErrors.accountHolderName = "Account holder name is required";
    }

    if (!editState.accountNumber?.trim()) {
      newErrors.accountNumber = "Bank account number is required";
    } else {
      const cleanAccountNumber = editState.accountNumber.replace(/\s/g, "");
      if (!pakistanAccountNumberRegex.test(cleanAccountNumber)) {
        newErrors.accountNumber = "Account number must be 10–16 digits.";
      }
    }

    if (!editState.bankName?.trim()) {
      newErrors.bankName = "Bank name is required";
    }else if(editState.bankName.length > 50){
      newErrors.bankName = "Bank name must be less than 50 characters";
    }else if(editState.bankName.length < 3){
      newErrors.bankName = "Bank name must be at least 3 characters";
    }

    if (!editState.bankBranchCode?.trim()) {
      newErrors.bankBranchCode = "Bank branch code is required";
    } else {
      const cleanBranchCode = editState.bankBranchCode.replace(/\s/g, "");
      if (!pakistanBranchCodeRegex.test(cleanBranchCode)) {
        newErrors.bankBranchCode = "Branch code must be 4 digits.";
      }
    }

    if (!editState.swiftCode?.trim()) {
      newErrors.swiftCode = "SWIFT/BIC code is required";
    } else {
      const cleanSwiftCode = editState.swiftCode.replace(/\s/g, "").toUpperCase();
      if (!pakistanSwiftCodeRegex.test(cleanSwiftCode)) {
        newErrors.swiftCode = "SWIFT must be 8 or 11 alphanumeric characters and include PK for Pakistan. Example: MCBPPKKA001";
      } else if (!cleanSwiftCode.includes("PK")) {
        newErrors.swiftCode = "SWIFT code must include 'PK' for Pakistan.";
      }
    }

    if (!editState.iban?.trim()) {
      newErrors.iban = "IBAN is required";
    } else {
      const cleanIban = editState.iban.replace(/\s/g, "").toUpperCase();
      if (!pakistanIbanRegex.test(cleanIban)) {
        newErrors.iban = "IBAN must be 24 chars, starting with 'PK'. Example: PK36HABB000000123456789012 ";
      }
    }

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoader(true);
    const { res, err } = await httpRequest<{
      message: string;
      createBankDetailsDto: IAccountInfo;
    }>({
      method: "put",
      path: isOperatorEdit
        ? `/paymentandwithdraw/update?id=${uid}`
        : "/paymentandwithdraw/update",
      params: editState,
    });
    if (res) {
      const { message, createBankDetailsDto } = res;
      toast(message || "Success", { type: "success" });
      onSuccess(createBankDetailsDto);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  useEffect(() => {
    setEditState(accountInfo);
  }, [accountInfo]);

  return (
    <form onSubmit={handleSubmit}>
      <div
        className={`grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 ${extraContainerClasses}`}
      >
        {allInputs.map((item: inputItems) => (
          <div key={item.key}>
            <SimpleInput
              value={editState ? editState[item.key] || "" : ""}
              type={item.type}
              isRequired={item.required}
              isLabel={true}
              labelText={item.label}
              minLength={item.minLength}
              maxLength={item.maxLength}
              onChange={(val) => {
                let value = val;
                if(item.key == "accountHolderName"){
                    value = val.replace(/[^a-zA-Z\s]/g, "");
                }
                handleChange(item.key, value , item?.validate);
                setError((prev) => ({ ...prev, [item.key]: "" }));
              }}
              error={error[item.key]}
              extraStyle={{ borderColor: "#009d91" }}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <PrimaryBtn
          loader={isLoader}
          type="submit"
          ButtonText={"Update"}
          btnExtraStyle={{ backgroundColor: "#00443f", borderRadius: "1.5rem" }}
        />
      </div>
    </form>
  );
};

export default EditPaymentInfo;
